'use client';

import { useEffect } from 'react';

export function GlobalRuntimeGuard() {
  useEffect(() => {
    const isProd = process.env.NODE_ENV === 'production';
    const safeGet = (key: string) => {
      try {
        return window.localStorage?.getItem(key);
      } catch {
        return null;
      }
    };
    const safeSet = (key: string, value: string) => {
      try {
        window.localStorage?.setItem(key, value);
      } catch {
        // ignore
      }
    };

    const currentBuild =
      typeof window !== 'undefined' ? (window as any).__NEXT_DATA__?.buildId : null;

    const storedBuild = safeGet('app-build-id');

    // If build changed since last visit, a new deploy happened. Clear the
    // sticky reload guard so the new build can self-heal too — without this
    // the guard would stay set forever after the first reload and users hitting
    // a chunk error AFTER a later deploy would never auto-recover.
    // (We only clear; we don't trigger an extra reload here — the page is
    // already loading the new build, no need to bounce again.)
    if (storedBuild && currentBuild && storedBuild !== currentBuild) {
      try {
        window.sessionStorage?.removeItem('app-reloaded');
      } catch {
        // ignore
      }
    }

    if (currentBuild) {
      safeSet('app-build-id', currentBuild);
    }

    const reloadOnce = (reason?: string) => {
      const KEY = 'app-reloaded';
      const hasReloaded = () => {
        try {
          return window.sessionStorage?.getItem(KEY) === '1';
        } catch {
          return typeof window.name === 'string' && window.name.includes(`${KEY}=1`);
        }
      };
      const markReloaded = () => {
        try {
          window.sessionStorage?.setItem(KEY, '1');
        } catch {
          // Fallback for Safari private mode / restricted storage
          window.name = `${window.name || ''} ${KEY}=1`;
        }
      };
      if (hasReloaded()) return;
      markReloaded();
      if (!isProd) {
        console.warn(
          '[GlobalRuntimeGuard] Reloading once due to runtime error:',
          reason ?? '(unknown)',
        );
      }
      window.location.reload();
    };

    const onError = (event: ErrorEvent) => {
      const msg = event?.message || '';
      const filename = event?.filename || '';
      const errName = (event as any)?.error?.name || '';

      if (
        msg.includes('ChunkLoadError') ||
        errName === 'ChunkLoadError' ||
        msg.includes('Loading chunk') ||
        msg.includes('Failed to fetch dynamically imported module') ||
        msg.includes('Load failed') || // iOS Safari/Chrome often reports this for chunk/script loads
        (msg.includes('Cannot read properties of undefined') &&
          filename.includes('_next/static/chunks')) ||
        // Webpack bootstrap failure: stale chunk after deploy. The user's
        // cached bundle has chunk IDs that no longer exist in the new
        // build, so webpack's module factory lookup returns undefined and
        // `__webpack_require__` throws this exact phrase. Same root cause
        // as ChunkLoadError, just a different surface — reload heals it.
        msg.includes("Cannot read properties of undefined (reading 'call')")
      ) {
        reloadOnce(`error event: "${msg}" (${errName}) @ ${filename}`);
      }
    };

    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      const reason = String(event?.reason || '');

      if (
        reason.includes('ChunkLoadError') ||
        reason.includes('Loading chunk') ||
        reason.includes('Failed to fetch dynamically imported module') ||
        reason.includes('Load failed')
      ) {
        reloadOnce(`unhandledrejection: ${reason}`);
      }
    };

    // Capture resource load errors (e.g., script chunk 404) — these do NOT always populate ErrorEvent.message.
    const onResourceError = (event: Event) => {
      const target = event.target as any;
      const tag = String(target?.tagName || '').toUpperCase();
      const src: string = target?.src || target?.href || '';
      if (!src) return;
      // Only react to Next.js asset failures.
      if (!src.includes('/_next/')) return;
      if (tag === 'SCRIPT' || tag === 'LINK') {
        reloadOnce(`resource error: ${tag} ${src}`);
      }
    };

    window.addEventListener('error', onError);
    window.addEventListener('error', onResourceError, true);
    window.addEventListener('unhandledrejection', onUnhandledRejection);

    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('error', onResourceError, true);
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
    };
  }, []);

  return null;
}
